import { createContext, useContext, useEffect } from "react";

import {
  AuthState,
  IAuthLoginResponse,
  IAuthService,
} from "../../../../modules/user/models/IAuthService";
import { UserStatus } from "../../../../modules/user/domain/constants";

import { useContextualNavigate } from "../../../../hooks/ContextualNavigations";
import useDialogAlert from "../../../../hooks/useDialogAlert";

import { routesConfig } from "../../../../config/routes";
import AppError from "../../../../utils/appError";

import { useIocContext } from "../../../../contexts/ioc/IocContext";
import { Types } from "../../../../ioc/types";

import {
  checkTokenInParams,
  getTokenDecodedFromUrl,
  resolveRedirectURL,
  setCookiesAccessToken,
  setCookiesRefreshToken,
  validateUrlParameters,
} from "./utilsLoginContext";
import { ILoginFormValues } from "../form/interface";
import { useParamsContext } from "../../../../contexts/params/ParamsContext";

interface ILoginContext {
  loginContext: {
    fetchLogin: (values) => void;
  };
}

interface ILoginProvider {
  children?: React.ReactNode;
}

const LoginContext = createContext<ILoginContext>(undefined);

const LoginProvider: React.FC<ILoginProvider> = ({ children }) => {
  const { snackbar } = useDialogAlert();
  const navigate = useContextualNavigate();

  const iocContext = useIocContext();
  const authService = iocContext.serviceContainer.get<IAuthService>(
    Types.User.IAuthService
  );

  const urlString = window.location.href;
  const { params } = useParamsContext();

  const fetchLogin = async (values: ILoginFormValues, isTokenUrl?: boolean) => {
    try {
      const { redirectURL, rules, redirectPath } =
        validateUrlParameters(params);
      values.rules = rules;

      const loginResponse: IAuthLoginResponse = await authService.authLogin(
        values
      );

      const {
        AccessToken = null,
        RefreshToken = null,
        state = null,
        extendedPassword = null,
        redirectURL: backendRedirectURL = null,
        meta: { custom_attributes: { userStatus = null } = {} } = {},
      } = loginResponse || {};

      switch (state) {
        case AuthState.TOO_MANY_ACCESSES:
          throw new Error(
            "Você atingiu o limite de tentativas, tente novamente em 10 segundos"
          );

        case AuthState.FAILED_SENT:
          throw new Error(
            "Tivemos um problema ao enviar o link de verificação para seu email"
          );

        case AuthState.SENT:
          return navigateToSecurityAuth(values);

        case AuthState.FIRST_ACCESS:
        case AuthState.FORCE_CHANGE_PASSWORD:
          return navigateToFirstAccess(values, extendedPassword);

        default:
          if (userStatus !== UserStatus.CONFIRMED) {
            return navigate(`${routesConfig.NEW_PASSWORD()}`);
          }
      }

      console.info("successfully Logged In.");
      setCookiesAccessToken(AccessToken);
      setCookiesRefreshToken(RefreshToken);

      // Prioriza URL dos parâmetros (fluxo legado); se ausente, usa a URL retornada pelo backend
      const finalURL = resolveRedirectURL(redirectURL, backendRedirectURL, redirectPath);

      document.location.href = finalURL;
    } catch (error) {
      if (error as AppError) {
        isTokenUrl
          ? snackbar({
              message: `O token da sua URL possui uma senha invalida, voce deve recuperar sua senha *** ${error?.message}`,
              variant: "error",
            })
          : snackbar({
              message: error?.message,
              variant: "error",
            });
      }
    }
  };

  const navigateToSecurityAuth = (values: ILoginFormValues) => {
    navigate(routesConfig.SECURITY_AUTH, {
      state: { email: values.email, password: values.password },
    });
  };

  const navigateToFirstAccess = (
    values: ILoginFormValues,
    extendedPassword: string | null
  ) => {
    navigate(`${routesConfig.UPDATE_PASSWORD_FIRST_ACCESS}`, {
      state: {
        password: extendedPassword || values.password,
        email: values.email,
        migration: Boolean(extendedPassword),
      },
    });
  };

  const fetchAutoLogin = async () => {
    if (!checkTokenInParams()) return;
    const decodedToken = getTokenDecodedFromUrl();
    const [email, password] = decodedToken.split(" ");
    const rules = params.rules;

    const valuesParams: ILoginFormValues = {
      email,
      password,
      rules,
    };

    await fetchLogin(valuesParams, true);
  };

  useEffect(() => {
    fetchAutoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlString]);

  const loginContext = {
    fetchLogin,
  };

  return (
    <LoginContext.Provider value={{ loginContext }}>
      {children}
    </LoginContext.Provider>
  );
};

const useLoginContext = () => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error("usePrice deve ser utilizado dentro de um LoginProvider");
  }
  return context;
};

export { LoginProvider, useLoginContext };
